import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import slugify from 'dibs-slugify/exports/slugify';
import { sessionStorage } from 'dibs-browser-storage';
import { useIntl, FormattedMessage } from 'dibs-react-intl';
import { trackFilterClick } from '../../../utils/tracking/searchBrowse/filterTracking';
import { ServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { handleMinimumFractionDigits } from 'dibs-buyer-price/exports/FormattedPrice';
import {
    isStatsFilter,
    isPriceFilter,
    isDimensionFilter,
    isFreeShippingFilter,
    constructStatsRange,
    isDeliveredByFilter,
    isLocalPickupFilter,
} from '../sbSharedRefineMenuHelpers';
import { sbSharedRefineMenuMessages } from '../../SbSharedRefineMenu/sbSharedRefineMenuMessages';

// components
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';

// styles
import styles from './SbSharedRefineMenuFacet.scss';

export const SESSION_STORAGE_KEY = 'facetMenu';

function updateLocalStorage({ isExpanded, storageFacetKey }) {
    const sessionFacetMenu = sessionStorage.getItem(SESSION_STORAGE_KEY) || {};
    sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        Object.assign(sessionFacetMenu, { [storageFacetKey]: isExpanded })
    );
}
function SbSharedRefineMenuFacet({
    title,
    children,
    appliedFiltersComponent,
    storageFacetKey,
    initiallyExpanded,
    selectedValues,
    measurementUnit,
    titleMessage,
    currency,
    maxStatsRange,
    localizedFilterValue,
}) {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
    const { isSbTopNavVariant: shouldIgnoreLocalStorageState } = useContext(ServerVarsContext);
    useEffect(() => {
        const sessionFacetMenu = sessionStorage.getItem(SESSION_STORAGE_KEY) || {};
        const isOpen = sessionFacetMenu[storageFacetKey];
        if (isOpen !== undefined && isExpanded !== isOpen && !shouldIgnoreLocalStorageState) {
            setIsExpanded(isOpen);
        }
    }, [storageFacetKey, isExpanded, shouldIgnoreLocalStorageState]);
    const onLabelClick = useCallback(() => {
        const newExpandedValue = !isExpanded;
        setIsExpanded(newExpandedValue);
        trackFilterClick(title, newExpandedValue);
        if (!shouldIgnoreLocalStorageState) {
            updateLocalStorage({ storageFacetKey, isExpanded: newExpandedValue });
        }
    }, [isExpanded, storageFacetKey, title, shouldIgnoreLocalStorageState]);
    const intl = useIntl();

    const renderAppliedFilters = () => {
        if (!selectedValues.length) {
            return [];
        }

        return selectedValues.map(appliedValue => {
            const displayName = appliedValue.displayName || null;
            const urlLabel = appliedValue.urlLabel || null;
            let value = displayName;
            let filterTitle = titleMessage;
            let dimension = null;

            if (isDimensionFilter(storageFacetKey)) {
                dimension = (
                    <span className={styles.dimension}>{measurementUnit.toLowerCase()}</span>
                );
            }

            if (isStatsFilter(storageFacetKey)) {
                const [min, max] = constructStatsRange(urlLabel, maxStatsRange).map(rangeValue => {
                    if (isPriceFilter(storageFacetKey)) {
                        return intl.formatNumber(rangeValue, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: handleMinimumFractionDigits(rangeValue),
                        });
                    } else {
                        return rangeValue;
                    }
                });
                value = (
                    <>
                        {min}&nbsp;{sbSharedRefineMenuMessages.TO}&nbsp;{max}
                    </>
                );
            } else if (isDeliveredByFilter(storageFacetKey)) {
                value = (
                    <FormattedMessage
                        id="sb.SbSharedRefineMenuFacet.valueWithinDays"
                        defaultMessage="Within {displayName} Days"
                        values={{ displayName }}
                    />
                );
            } else if (isLocalPickupFilter(storageFacetKey)) {
                value = localizedFilterValue;
            } else if (isFreeShippingFilter(storageFacetKey)) {
                filterTitle = sbSharedRefineMenuMessages.shippingOptions;
                value = sbSharedRefineMenuMessages.shippingFree;
            }

            return (
                <div key={`${storageFacetKey}-${value}`} className={styles.abTestSelectedValue}>
                    {value || filterTitle}
                    {dimension}
                </div>
            );
        });
    };

    return (
        <ExpandingArea
            showArrow
            dataTn={`facet-${slugify(title)}-expanding-area`}
            onClick={onLabelClick}
            label={
                <>
                    <div className={styles.title}>{title}</div>
                    {!isExpanded && renderAppliedFilters()}
                    {appliedFiltersComponent}
                </>
            }
            expanded={isExpanded}
            labelClass={styles.titleWrapper}
            wrapperClass={styles.container}
            expandingAreaClass={styles.facetContent}
        >
            {children}
        </ExpandingArea>
    );
}

SbSharedRefineMenuFacet.defaultProps = {
    initiallyExpanded: true,
    selectedValues: [],
    measurementUnit: '',
    titleMessage: '',
    currency: '',
    maxStatsRange: '',
    localizedFilterValue: '',
};

SbSharedRefineMenuFacet.propTypes = {
    initiallyExpanded: PropTypes.bool,
    title: PropTypes.string.isRequired,
    appliedFiltersComponent: PropTypes.node,
    children: PropTypes.node,
    storageFacetKey: PropTypes.string.isRequired,
    selectedValues: PropTypes.array,
    measurementUnit: PropTypes.string,
    titleMessage: PropTypes.string,
    currency: PropTypes.string,
    maxStatsRange: PropTypes.string,
    localizedFilterValue: PropTypes.string,
};

export { SbSharedRefineMenuFacet };
